import React, { useCallback, useEffect, useState } from "react";
import { TFunction, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ProductApi } from "src/services/Api/Organization/types";
import { api } from "src/services/Api/api";
import useFlash from "go-alert/AlertMessage";
import Header from "go-app/components/Header";
import handleError from "go-app/services/errors";
import ImportCsv from "go-component/components/ImportCsv/ImportCsv";
import { camelToSnakeCase } from "go-component/utils/componentUtils";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { LoadingContainer } from "go-core/components/Loading";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { CustomFieldEntityApi, CustomFieldTemplateApi } from "go-segment/services/types";

const getTranslationsConfig = (t: TFunction) => {
	return {
		export_csv: t("modules.product.action.export_csv.title"),
		resource_name: t("modules.product.field.resource_name.title"),
		constraints: {
			cannot_be_empty: t("constraints.cannot_be_empty"),
			csv_corrupted: t("constraints.csv_corrupted"),
			duplicate_name: t("constraints.duplicate_name"),
			duplicate_tax_id_no: t("constraints.duplicate_tax_id_no"),
			invalid_custom_field_option: t("constraints.invalid_custom_field_option"),
			invalid_custom_field_resource: t("constraints.invalid_custom_field_resource"),
			invalid_custom_field_value: t("constraints.invalid_custom_field_value"),
			invalid_email: t("constraints.invalid_email"),
			invalid_value: t("constraints.invalid_value"),
			max_length: t("constraints.max_length"),
		},
		database_column_name: {
			uniqueId: t("enums.resource_type.product.uniqueId"),
			name: t("enums.resource_type.product.name"),
			sku: t("enums.resource_type.product.sku"),
			category: t("enums.resource_type.product.category"),
			measureType: t("enums.resource_type.product.measureType"),
			description: t("enums.resource_type.product.description"),
			tax: t("enums.resource_type.product.tax"),
			salePriceNet: t("enums.resource_type.product.salePriceNet"),
			barcode: t("enums.resource_type.product.barcode"),
			defaultVolume: t("enums.resource_type.product.defaultVolume"),
			criticalLevelAmount: t("enums.resource_type.product.criticalLevelAmount"),
			optimalLevelAmount: t("enums.resource_type.product.optimalLevelAmount"),
			connections: t("enums.resource_type.product.connections"),
			referencePriceSetAt: t("enums.resource_type.product.referencePriceSetAt"),
			referencePrice: t("enums.resource_type.product.referencePrice"),
		},
	};
};

export const OrganizationProductImportPage = () => {
	const { t } = useTranslation();
	const [customFieldsConfig, setCustomFieldsConfig] = useState<CustomFieldTemplateApi[] | undefined>(undefined);
	const { addFlash } = useFlash();
	const organization = useSelector(selectOrganization);
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		handleChangeTabTitle(t("modules.product.action.import_from_csv.title"));
	}, []);

	const onFetchCustomFields = useCallback(async () => {
		const params: Record<string, string> = {
			resource_name: "PRODUCT",
			custom_field_resource_type: "PRODUCT",
			custom_field_permission_scope: "WRITE",
		};
		try {
			const res = await api.organization().getResourceTypes(params);
			setCustomFieldsConfig(res.custom_fields);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	}, [addFlash]);

	useEffect(() => {
		onFetchCustomFields();
	}, [onFetchCustomFields]);

	const comparationCheck = (data: ProductApi, fieldName: string) => {
		if (!data) return false;
		switch (fieldName) {
			case "category":
				return data.category?.name;
			case "tax":
				return data.tax?.name;
			case "sale_price_net":
				return data.sale_price_net_money?.amount;
			case "critical_level_amount":
				return data.critical_amount_level;
			case "optimal_level_amount":
				return data.optimal_amount_level;
			case "connections":
				return JSON.stringify(data.connections);
			case "barcodes":
				return JSON.stringify(data.custom_ids?.filter((customId) => customId.type === "BARCODE"));
			case "reference_price":
				return FormatMoney(data.cost_price_money);
			default:
				return data[fieldName as keyof ProductApi];
		}
	};

	const renderComparedValue = (data: ProductApi, fieldName: string, differs?: boolean) => {
		if (!data) return <span></span>;
		let caption, title;

		const replacedCf = fieldName.replace("+", "");
		const cf = data?.custom_fields?.find(
			(x: CustomFieldEntityApi) => x.slug === camelToSnakeCase(replacedCf, true)
		);
		if (cf && Object.keys(cf).length > 0) {
			caption = cf.value;
			title = cf.value;
		} else {
			switch (fieldName) {
				case "measure_type":
					title = t(`enums.products.measure_types.${data.measure_type}`);
					caption = title;
					break;
				case "category":
					title = data.category?.name;
					caption = title;
					break;
				case "tax":
					title = data.tax?.name;
					caption = title;
					break;
				case "sale_price_net":
					title = FormatMoney(data.sale_price_net_money);
					caption = title;
					break;
				case "critical_level_amount":
					title = data.critical_amount_level?.toString();
					caption = title;
					break;
				case "optimal_level_amount":
					title = data.optimal_amount_level?.toString();
					caption = title;
					break;
				case "connections":
					title = data.connections?.map((connection) => connection.name).join(", ");
					caption = title;
					break;
				case "barcode":
					title = data.custom_ids
						?.filter((customId) => customId.type === "BARCODE")
						.map((customId) => customId.value)
						.join(", ");
					caption = title;
					break;
				case "reference_price":
					title = FormatMoney(data.cost_price_money);
					caption = title;
					break;
				default:
					title = data[fieldName as keyof ProductApi]?.toString();
					caption = title;
			}
		}
		return (
			<span style={{ textDecoration: `${differs ? "line-through" : "none"}` }} title={title}>
				{caption}
			</span>
		);
	};

	if (!customFieldsConfig) return <LoadingContainer />;

	return (
		<>
			<Header title={t("modules.product.action.import_from_csv.title")} />
			<ImportCsv
				resourceType="products"
				customFieldsConfig={customFieldsConfig}
				redirectionUrl={`/${organization.id}/products`}
				importCsvRequestParams="all"
				comparationCheck={comparationCheck}
				renderComparedValue={renderComparedValue}
				translations={getTranslationsConfig(t)}
				workingInBackground
			/>
		</>
	);
};
